import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, FileDown, RefreshCw, FileText, Sparkles, X,
} from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const STATUSES = ["applied", "shortlisted", "interview", "selected", "rejected"];
const STATUS_STYLE = {
  applied: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  shortlisted: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  interview: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  selected: "bg-green-500/15 text-green-300 border-green-500/40",
  rejected: "bg-red-500/15 text-red-300 border-red-500/40",
};

const emptyJob = {
  title: "", department: "Engineering", location: "Hyderabad, IN",
  type: "Full-time", experience: "", description: "", is_open: true,
};

const defaultJoiningDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
};

const emptyOfferForm = (app = null, job = null) => ({
  candidate_name: app?.name || "",
  candidate_email: app?.email || "",
  role_title: job?.title || app?.job_title || "",
  department: job?.department || "General",
  location: job?.location || "Hyderabad, IN",
  ctc_inr: 900000,
  joining_date: defaultJoiningDate(),
  reporting_to: "Hiring Manager",
});

const OfferForm = ({ initial, onSubmit, busy, submitLabel, templates = [] }) => {
  const [v, setV] = useState(initial);
  const set = (k) => (e) => {
    const val = e.target.value;
    setV((s) => {
      const next = { ...s, [k]: val };
      // Auto-pick template when department changes (unless user already chose one)
      if (k === "department" && templates.length) {
        const dept = val.trim().toLowerCase();
        const match = templates.find((t) =>
          (t.match_departments || []).some((m) => m.toLowerCase() === dept));
        next.template_id = (match || templates.find((t) => t.id === "general") || {}).id || s.template_id;
      }
      return next;
    });
  };
  const input =
    "bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500";
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(v); }}
      className="p-6 space-y-3"
      data-testid="offer-form"
    >
      <div className="grid grid-cols-2 gap-3">
        <input required placeholder="Candidate name *" value={v.candidate_name}
               onChange={set("candidate_name")} data-testid="offer-name" className={input} />
        <input required type="email" placeholder="Candidate email *" value={v.candidate_email}
               onChange={set("candidate_email")} data-testid="offer-email" className={input} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input required placeholder="Role title *" value={v.role_title}
               onChange={set("role_title")} data-testid="offer-role" className={input} />
        <input required placeholder="Department *" value={v.department}
               onChange={set("department")} data-testid="offer-department" className={input} />
        <input required placeholder="Location *" value={v.location}
               onChange={set("location")} data-testid="offer-location" className={input} />
        <input required type="number" min="0" placeholder="Annual CTC (INR) *" value={v.ctc_inr}
               onChange={set("ctc_inr")} data-testid="offer-ctc" className={input} />
        <input required type="date" value={v.joining_date}
               onChange={set("joining_date")} data-testid="offer-joining" className={input} />
        <input required placeholder="Reporting to *" value={v.reporting_to}
               onChange={set("reporting_to")} data-testid="offer-reporting" className={input} />
      </div>
      {templates.length > 0 && (
        <div>
          <label className="overline mb-1.5 block">Offer template</label>
          <select
            value={v.template_id || ""}
            onChange={set("template_id")}
            data-testid="offer-template"
            className={input + " w-full"}
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id} className="bg-[#0B132B]">
                {t.label} — {(t.clauses || []).length} clauses · {(t.benefits || []).length} benefits
              </option>
            ))}
          </select>
          <div className="mt-1.5 text-[10px] font-mono text-slate-500 tracking-widest uppercase">
            Compensation text auto-adjusts per department
          </div>
        </div>
      )}
      <button type="submit" disabled={busy}
              data-testid="offer-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm font-medium px-4 py-3 rounded-sm">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {submitLabel || "Generate & send"}
      </button>
    </form>
  );
};

const OfferModal = ({ title, initial, onClose, onSubmit, busy, templates }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}
       data-testid="offer-modal">
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
    <div className="relative w-full max-w-xl bg-[#0B132B] border border-white/10 max-h-[92vh] overflow-y-auto"
         onClick={(e) => e.stopPropagation()}>
      <div className="p-5 border-b border-white/10 flex items-start justify-between">
        <div>
          <div className="overline">Offer Letter</div>
          <h3 className="mt-2 font-display text-white text-xl">{title}</h3>
          <p className="mt-1 text-slate-500 text-xs">
            Generates a branded PDF, emails it to the candidate with an accept/decline link, and saves a copy for audit.
          </p>
        </div>
        <button onClick={onClose} data-testid="offer-modal-close"
                className="text-slate-400 hover:text-white"><X size={18} /></button>
      </div>
      <OfferForm initial={initial} onSubmit={onSubmit} busy={busy} templates={templates} />
    </div>
  </div>
);

const TemplateEditor = ({ template, onSave }) => {
  const [v, setV] = useState({
    clauses: (template.clauses || []).join("\n"),
    benefits: (template.benefits || []).join("\n"),
    notice_period_days: template.notice_period_days ?? 30,
    probation_months: template.probation_months ?? 3,
  });
  const [busy, setBusy] = useState(false);
  const input =
    "w-full bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500";
  const save = async () => {
    setBusy(true);
    try {
      await onSave(template.id, {
        clauses: v.clauses.split("\n").map((s) => s.trim()).filter(Boolean),
        benefits: v.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
        notice_period_days: Number(v.notice_period_days) || 30,
        probation_months: Number(v.probation_months) || 3,
      });
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`template-editor-${template.id}`}
         className="bg-[#0B132B]/40 border border-white/10 p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-red-500">{template.id}</div>
          <div className="mt-1 text-white font-medium">{template.label}</div>
          <div className="text-slate-500 text-xs mt-0.5">
            Auto-selected for: {(template.match_departments || []).join(", ") || "fallback (all departments)"}
          </div>
        </div>
        <button onClick={save} disabled={busy}
                data-testid={`template-save-${template.id}`}
                className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-xs px-3 py-2 rounded-sm">
          {busy ? <Loader2 size={12} className="animate-spin" /> : null} Save
        </button>
      </div>
      <div className="mt-4 grid gap-3">
        <div>
          <label className="overline mb-1.5 block">Compensation clauses (one per line)</label>
          <textarea rows={4} value={v.clauses}
                    onChange={(e) => setV({ ...v, clauses: e.target.value })}
                    data-testid={`template-clauses-${template.id}`}
                    className={input} />
        </div>
        <div>
          <label className="overline mb-1.5 block">Benefits (one per line)</label>
          <textarea rows={3} value={v.benefits}
                    onChange={(e) => setV({ ...v, benefits: e.target.value })}
                    data-testid={`template-benefits-${template.id}`}
                    className={input} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="overline mb-1.5 block">Notice period (days)</label>
            <input type="number" min="0" value={v.notice_period_days}
                   onChange={(e) => setV({ ...v, notice_period_days: e.target.value })}
                   className={input} />
          </div>
          <div>
            <label className="overline mb-1.5 block">Probation (months)</label>
            <input type="number" min="0" value={v.probation_months}
                   onChange={(e) => setV({ ...v, probation_months: e.target.value })}
                   className={input} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdminCareers() {
  const { apiBase, authHeader } = useAuth();
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [directOffers, setDirectOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyJob);
  const [busy, setBusy] = useState(false);
  const [offerFor, setOfferFor] = useState(null);   // application being offered
  const [showDirect, setShowDirect] = useState(false);
  const [offerBusy, setOfferBusy] = useState(false);
  const [templates, setTemplates] = useState([]);

  const loadTemplates = useCallback(async () => {
    const { data } = await axios.get(`${apiBase}/admin/careers/offer-templates`, { headers: authHeader() });
    setTemplates(data);
  }, [apiBase, authHeader]);

  const loadJobs = useCallback(async () => {
    const { data } = await axios.get(`${apiBase}/admin/careers/jobs`, { headers: authHeader() });
    setJobs(data);
  }, [apiBase, authHeader]);

  const loadApps = useCallback(async () => {
    const { data } = await axios.get(`${apiBase}/admin/careers/applications`, { headers: authHeader() });
    setApps(data);
  }, [apiBase, authHeader]);

  const loadDirectOffers = useCallback(async () => {
    const { data } = await axios.get(`${apiBase}/admin/careers/direct-offers`, { headers: authHeader() });
    setDirectOffers(data);
  }, [apiBase, authHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    try { await Promise.all([loadJobs(), loadApps(), loadDirectOffers(), loadTemplates()]); }
    catch { toast.error("Failed to load careers data"); }
    finally { setLoading(false); }
  }, [loadJobs, loadApps, loadDirectOffers, loadTemplates]);

  useEffect(() => { load(); }, [load]);

  const jobsById = useMemo(() => Object.fromEntries(jobs.map((j) => [j.id, j])), [jobs]);

  const generateOfferForApp = async (values) => {
    setOfferBusy(true);
    try {
      await axios.post(
        `${apiBase}/admin/careers/applications/${offerFor.id}/generate-offer`,
        { ...values, ctc_inr: Number(values.ctc_inr) || 0 },
        { headers: authHeader() },
      );
      toast.success("Offer letter generated, emailed and saved");
      setOfferFor(null);
      await loadApps();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Offer generation failed");
    } finally { setOfferBusy(false); }
  };

  const generateDirectOffer = async (values) => {
    setOfferBusy(true);
    try {
      const resp = await axios.post(
        `${apiBase}/admin/careers/direct-offer`,
        { ...values, ctc_inr: Number(values.ctc_inr) || 0 },
        { headers: authHeader(), responseType: "blob" },
      );
      const offerNo = resp.headers["x-offer-no"] || "OFFER";
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${offerNo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Offer ${offerNo} generated, emailed and saved`);
      setShowDirect(false);
      await loadDirectOffers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Direct offer failed");
    } finally { setOfferBusy(false); }
  };

  const saveTemplate = async (templateId, patch) => {
    try {
      await axios.put(
        `${apiBase}/admin/careers/offer-templates/${templateId}`,
        patch, { headers: authHeader() },
      );
      toast.success("Template saved");
      await loadTemplates();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Template save failed");
    }
  };

  const createJob = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${apiBase}/admin/careers/jobs`, form, { headers: authHeader() });
      toast.success("Role posted");
      setForm(emptyJob); setShowForm(false);
      await loadJobs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  const toggleJobOpen = async (job) => {
    try {
      await axios.patch(`${apiBase}/admin/careers/jobs/${job.id}`,
        { is_open: !job.is_open }, { headers: authHeader() });
      await loadJobs();
    } catch { toast.error("Update failed"); }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? Existing applications remain.`)) return;
    try {
      await axios.delete(`${apiBase}/admin/careers/jobs/${job.id}`, { headers: authHeader() });
      await loadJobs();
    } catch { toast.error("Delete failed"); }
  };

  const setAppStatus = async (app, status) => {
    try {
      const { data } = await axios.patch(
        `${apiBase}/admin/careers/applications/${app.id}`,
        { status }, { headers: authHeader() },
      );
      setApps((rows) => rows.map((r) => (r.id === app.id ? data : r)));
      if (status === "selected") toast.success("Offer letter generated and emailed to candidate");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const downloadFile = async (url, filename) => {
    try {
      const resp = await axios.get(url, { headers: authHeader(), responseType: "blob" });
      const blob = new Blob([resp.data]);
      const bu = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = bu; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(bu);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="admin-careers-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="overline">/Admin · Careers</span>
          <h1 className="mt-4 font-display text-white text-4xl md:text-5xl tracking-tighter font-light">
            Hiring pipeline
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDirect(true)}
            data-testid="admin-careers-direct-offer"
            className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm px-4 py-2.5 rounded-sm"
          >
            <Sparkles size={14} /> Direct Offer
          </button>
          <button
            onClick={load} disabled={loading}
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white text-sm px-4 py-2.5 rounded-sm"
            data-testid="admin-careers-refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-2 border-b border-white/10">
        {[
          { id: "jobs", label: `Roles · ${jobs.length}` },
          { id: "apps", label: `Applications · ${apps.length}` },
          { id: "offers", label: `Direct Offers · ${directOffers.length}` },
          { id: "templates", label: `Templates · ${templates.length}` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-careers-tab-${t.id}`}
            className={`px-4 py-2.5 text-xs font-mono tracking-[0.14em] uppercase transition-colors ${
              tab === t.id ? "text-white border-b-2 border-red-500" : "text-slate-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "jobs" && (
        <div className="mt-6">
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowForm((s) => !s)}
              className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm px-4 py-2.5 rounded-sm"
              data-testid="admin-careers-new-job"
            >
              <Plus size={14} /> {showForm ? "Cancel" : "New role"}
            </button>
          </div>

          {showForm && (
            <form onSubmit={createJob} data-testid="admin-careers-job-form"
                  className="bg-[#0B132B]/50 border border-white/10 p-5 mb-6 space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <input required placeholder="Title *" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="admin-careers-job-title"
                  className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
                <input required placeholder="Department *" value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
                <input required placeholder="Location *" value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
                <input placeholder="Experience (e.g. 3-5 years)" value={form.experience}
                  onChange={(e) => setForm({ ...form, experience: e.target.value })}
                  className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
              </div>
              <textarea required placeholder="Role description *" rows={4} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="admin-careers-job-description"
                className="w-full bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
              <button type="submit" disabled={busy}
                data-testid="admin-careers-job-submit"
                className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm px-4 py-2.5 rounded-sm">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Post role
              </button>
            </form>
          )}

          <div className="bg-[#0B132B]/40 border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  {["Title", "Dept · Loc", "Type", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center">
                    <Loader2 size={18} className="inline animate-spin text-slate-500" />
                  </td></tr>
                )}
                {!loading && jobs.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500 text-sm">
                    No roles yet — post one to open your careers page.
                  </td></tr>
                )}
                {jobs.map((j) => (
                  <tr key={j.id} data-testid={`admin-careers-job-row-${j.id}`}
                      className="border-b border-white/5 last:border-0">
                    <td className="px-4 py-4 text-white">{j.title}</td>
                    <td className="px-4 py-4 text-slate-300 text-xs">
                      {j.department} · {j.location}
                    </td>
                    <td className="px-4 py-4 text-slate-400 text-xs">{j.type}</td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => toggleJobOpen(j)}
                        data-testid={`admin-careers-job-toggle-${j.id}`}
                        className={`inline-flex px-2.5 py-1 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase ${
                          j.is_open ? "bg-green-500/15 text-green-300 border-green-500/40" :
                                       "bg-slate-500/15 text-slate-400 border-slate-500/40"
                        }`}
                      >
                        {j.is_open ? "Open" : "Closed"}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <button onClick={() => deleteJob(j)}
                              data-testid={`admin-careers-job-delete-${j.id}`}
                              className="text-slate-500 hover:text-red-400">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "apps" && (
        <div className="mt-6 bg-[#0B132B]/40 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {["Candidate", "Role", "Applied", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-4 py-12 text-center">
                  <Loader2 size={18} className="inline animate-spin text-slate-500" />
                </td></tr>
              )}
              {!loading && apps.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500 text-sm">
                  No applications yet.
                </td></tr>
              )}
              {apps.map((a) => (
                <tr key={a.id} data-testid={`admin-careers-app-row-${a.id}`}
                    className="border-b border-white/5 last:border-0">
                  <td className="px-4 py-4">
                    <div className="text-white">{a.name}</div>
                    <div className="text-slate-500 text-xs">{a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
                    {a.offer?.status && (
                      <div className="mt-1">
                        <span className={`inline-flex px-2 py-0.5 rounded-sm border font-mono text-[9px] tracking-[0.14em] uppercase ${
                          a.offer.status === "accepted" ? "bg-green-500/15 text-green-300 border-green-500/40" :
                          a.offer.status === "declined" ? "bg-red-500/15 text-red-300 border-red-500/40" :
                          "bg-yellow-500/15 text-yellow-300 border-yellow-500/40"
                        }`}>
                          Offer {a.offer.status}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-slate-300 text-xs">
                    {a.job_title || jobsById[a.job_id]?.title || "—"}
                  </td>
                  <td className="px-4 py-4 text-slate-500 text-xs font-mono">
                    {new Date(a.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td className="px-4 py-4">
                    <select
                      value={a.status}
                      onChange={(e) => setAppStatus(a, e.target.value)}
                      data-testid={`admin-careers-app-status-${a.id}`}
                      className={`px-2 py-1 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase focus:outline-none ${STATUS_STYLE[a.status] || STATUS_STYLE.applied}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-[#0B132B] text-white">{s}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => downloadFile(
                          `${apiBase}/admin/careers/applications/${a.id}/resume`,
                          a.resume?.filename || `resume-${a.id}.pdf`,
                        )}
                        data-testid={`admin-careers-app-resume-${a.id}`}
                        className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-xs"
                      >
                        <FileDown size={12} /> Resume
                      </button>
                      <button
                        onClick={() => setOfferFor(a)}
                        data-testid={`admin-careers-app-generate-offer-${a.id}`}
                        className="inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 text-xs"
                        title="Generate a fresh offer letter with your own terms"
                      >
                        <Sparkles size={12} /> {a.offer ? "Regenerate offer" : "Generate offer"}
                      </button>
                      {a.offer && (
                        <button
                          onClick={() => downloadFile(
                            `${apiBase}/admin/careers/applications/${a.id}/offer-letter`,
                            `${a.offer.offer_no}.pdf`,
                          )}
                          data-testid={`admin-careers-app-offer-${a.id}`}
                          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-xs"
                        >
                          <FileText size={12} /> PDF
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tab === "offers" && (
        <div className="mt-6 bg-[#0B132B]/40 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {["Offer #", "Candidate", "Role", "Joining", "Status", "Issued", "PDF"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-12 text-center">
                  <Loader2 size={18} className="inline animate-spin text-slate-500" />
                </td></tr>
              )}
              {!loading && directOffers.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500 text-sm">
                  No direct offers yet. Click "Direct Offer" above to generate one without an application.
                </td></tr>
              )}
              {directOffers.map((o) => (
                <tr key={o.offer_no} data-testid={`admin-offer-row-${o.offer_no}`}
                    className="border-b border-white/5 last:border-0">
                  <td className="px-4 py-4 text-white font-mono text-xs">{o.offer_no}</td>
                  <td className="px-4 py-4">
                    <div className="text-white">{o.candidate_name}</div>
                    <div className="text-slate-500 text-xs">{o.candidate_email}</div>
                  </td>
                  <td className="px-4 py-4 text-slate-300 text-xs">{o.role_title}</td>
                  <td className="px-4 py-4 text-slate-400 text-xs font-mono">{o.joining_date}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex px-2.5 py-1 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase ${
                      o.status === "accepted" ? "bg-green-500/15 text-green-300 border-green-500/40" :
                      o.status === "declined" ? "bg-red-500/15 text-red-300 border-red-500/40" :
                      "bg-yellow-500/15 text-yellow-300 border-yellow-500/40"
                    }`}>{o.status || "sent"}</span>
                  </td>
                  <td className="px-4 py-4 text-slate-500 text-xs font-mono">
                    {new Date(o.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => downloadFile(
                        `${apiBase}/admin/careers/direct-offers/${o.offer_no}/download`,
                        `${o.offer_no}.pdf`,
                      )}
                      data-testid={`admin-offer-download-${o.offer_no}`}
                      className="inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 text-xs"
                    >
                      <FileDown size={12} /> Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "templates" && (
        <div className="mt-6 space-y-4" data-testid="admin-careers-templates">
          <p className="text-slate-500 text-sm">
            Templates auto-match by department (Engineering / Sales / Design / Operations) —
            the compensation clauses and benefits in the offer letter adapt automatically.
          </p>
          {templates.map((t) => (
            <TemplateEditor key={t.id} template={t} onSave={saveTemplate} />
          ))}
        </div>
      )}

      {/* Per-application offer modal */}
      {offerFor && (
        <OfferModal
          title={`Offer for ${offerFor.name}`}
          initial={emptyOfferForm(offerFor, jobsById[offerFor.job_id])}
          busy={offerBusy}
          templates={templates}
          onClose={() => setOfferFor(null)}
          onSubmit={generateOfferForApp}
        />
      )}

      {/* Direct offer (no application) */}
      {showDirect && (
        <OfferModal
          title="Direct offer (no application)"
          initial={emptyOfferForm()}
          busy={offerBusy}
          templates={templates}
          onClose={() => setShowDirect(false)}
          onSubmit={generateDirectOffer}
        />
      )}
    </div>
  );
}
